import React, { useState, useEffect, useCallback } from 'react';
import { IconTrash, IconDownload } from './Icons';
import { TENDER_DOC_TYPES } from '../data/tendersMock';
import { createClient } from '@supabase/supabase-js';
import { getPortalUrlForEmails, sendEmail } from '../services/emailService';
import './Tenders.css';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const noOpLock = async (_name, _acquireTimeout, fn) => fn();
const tenderFormSupabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, lock: noOpLock },
});

const GEMINI_API_KEY = "AIzaSyC3SZeOmzHvPjfnTB3sBmMR0cZJBOZbvfE";
const GEMINI_URL = "https://generativelanguage.googleapis.com/v1beta/models/gemini-3-flash-preview:generateContent";

const GEMINI_PROMPT = `You are an expert tender / RFP analyst. Analyze ALL provided tender documents (notices, RFPs, clarifications, etc.).

RULES:
- Return null for any field not explicitly found — never guess
- Dates must be YYYY-MM-DD if determinable, otherwise return the raw text found
- description: 150-200 word summary of what the vendor must deliver
- Translate any Arabic content to English
- submissionDeadline may appear in a separate notice — check all files

Return ONLY a raw JSON object, no markdown, no backticks, no explanation:

{
  "title": null,
  "referenceNumber": null,
  "department": null,
  "projectLocation": null,
  "issuingAuthority": null,
  "description": null,
  "issueDate": null,
  "clarificationDeadline": null,
  "submissionDeadline": null,
  "openingDate": null,
  "expectedAwardDate": null,
  "tenderValidityDays": null,
  "projectDuration": null,
  "currency": null
}`;

const toBase64 = (file) =>
  new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const result = reader.result || "";
        const base64 = String(result).split(",")[1] || "";
        res(base64);
      } catch (err) {
        rej(err);
      }
    };
    reader.onerror = () => rej(new Error("Read failed"));
    reader.readAsDataURL(file);
  });

const TENDER_FORM_TABS = [
  'Documents',
  'General Information',
  'Timeline',
  'Financial',
  'Assignment',
  'Review & Approval',
];

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB per file
const TENDER_DOCS_BUCKET = 'tender-documents';

/** Calendar date arithmetic (YYYY-MM-DD, UTC) for reminder offset from submission deadline. */
function addDaysToIsoDate(iso, daysDelta) {
  if (!iso || String(iso).length < 10) return null;
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split('-').map((x) => parseInt(x, 10));
  if (!y || !m || !d) return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + daysDelta);
  return dt.toISOString().slice(0, 10);
}

/** Planned calendar date for the reminder from current preset (ignores reminder checkbox). */
function plannedReminderDate(submissionDeadline, form) {
  if (!submissionDeadline) return null;
  if (form?.assignmentReminderPreset === 'custom') {
    const c = (form.assignmentReminderCustomDate || '').trim().slice(0, 10);
    return c || null;
  }
  const n = Number(form?.assignmentReminderPreset);
  if (Number.isNaN(n) || n < 0) return null;
  return addDaysToIsoDate(submissionDeadline, -n);
}

function computeReminderCalendarDate(submissionDeadline, form) {
  if (!submissionDeadline || !form?.assignmentReminderEmail) return null;
  return plannedReminderDate(submissionDeadline, form);
}

function describeReminderPreset(form) {
  if (!form?.assignmentReminderEmail) return '';
  if (form.assignmentReminderPreset === 'custom') return 'on your selected reminder date';
  const n = Number(form.assignmentReminderPreset);
  if (Number.isNaN(n)) return '';
  if (n === 0) return 'on the submission deadline';
  if (n === 1) return '1 day before the submission deadline';
  return `${n} days before the submission deadline`;
}

const TIMELINE_FIELDS = [
  { key: 'issueDate', label: 'Issue date' },
  { key: 'clarificationDeadline', label: 'Clarification deadline' },
  { key: 'submissionDeadline', label: 'Submission deadline', required: true },
  { key: 'openingDate', label: 'Opening date' },
  { key: 'expectedAwardDate', label: 'Expected award date' },
];

const REMINDER_PRESET_OPTIONS = [
  { value: '0', label: 'On submission deadline' },
  { value: '1', label: '1 day before submission deadline' },
  { value: '2', label: '2 days before submission deadline' },
  { value: '3', label: '3 days before submission deadline' },
  { value: '7', label: '7 days before submission deadline' },
  { value: 'custom', label: 'Custom reminder date…' },
];

const defaultTenderForm = () => ({
  title: '',
  referenceNumber: '',
  departmentId: '',
  department: '',
  projectLocation: '',
  issuingAuthority: '',
  description: '',
  issueDate: '',
  clarificationDeadline: '',
  submissionDeadline: '',
  openingDate: '',
  expectedAwardDate: '',
  tenderValidityDays: '',
  estimatedValue: '',
  currency: 'OMR',
  bidFee: '',
  tenderBondRequired: false,
  bondAmount: '',
  projectDuration: '',
  assignedEmployeeId: '',
  assignedEmployee: '',
  proposalSubmissionDeadline: '',
  assignmentReminderEmail: false,
  assignmentReminderPreset: '2',
  assignmentReminderCustomDate: '',
  internalReviewStatus: 'Pending',
  approvalRequired: true,
  approvalNotes: '',
});

const defaultDoc = (file) => ({
  id: `doc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  filename: file?.name || 'New file.pdf',
  displayName: file?.name || '',
  size: file?.size ?? null,
  documentType: 'Tender Notice',
  version: '1',
  uploadDate: new Date().toISOString().slice(0, 10),
  uploadedBy: 'Current User',
  file: file || null, // raw File for upload
});

function buildInitialForm(editTender) {
  const base = defaultTenderForm();
  if (!editTender) return base;
  const merged = {
    ...base,
    ...editTender,
    assignedEmployeeId: editTender.assignedToUserId ?? editTender.assignedEmployeeId ?? '',
  };
  merged.assignmentReminderEmail = editTender.assignmentReminderEmail === true;
  merged.assignmentReminderCustomDate = editTender.assignmentReminderCustomDate || '';
  merged.assignmentReminderPreset = editTender.assignmentReminderCustomDate
    ? 'custom'
    : ['0', '1', '2', '3', '7'].includes(String(editTender.assignmentReminderDaysBefore ?? ''))
      ? String(editTender.assignmentReminderDaysBefore)
      : '2';
  return merged;
}

export default function TenderForm({ onClose, onSuccess, editTender = null }) {
  const [activeTab, setActiveTab] = useState(0);
  const [form, setForm] = useState(() => buildInitialForm(editTender));
  const [documents, setDocuments] = useState(editTender?.documents || []);
  const [submitting, setSubmitting] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [docError, setDocError] = useState('');
  const [aiFilling, setAiFilling] = useState(false);
  const [aiUsed, setAiUsed] = useState(false);
  const [toast, setToast] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [removedDocs, setRemovedDocs] = useState([]); // { id, storage_path } for docs removed when editing
  const [documentsLoading, setDocumentsLoading] = useState(false);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const pickProfileEmail = useCallback((profile) => {
    if (!profile) return '';
    return (
      profile.work_email ||
      profile.email ||
      profile.user_email ||
      profile.contact_email ||
      profile.mail ||
      ''
    )
      .toString()
      .trim();
  }, []);

  const maybeNotifyAssignedEmployee = useCallback(
    async ({ payload, tenderRecordId, tenderCode, form: formSnap }) => {
      const assignedUserId = payload?.assigned_to_user_id || '';
      const proposalDeadline = payload?.proposal_submission_deadline || '';
      if (!assignedUserId || !proposalDeadline) return;

      const previousAssigned =
        editTender?.assignedToUserId ||
        editTender?.assignedEmployeeId ||
        '';
      const previousDeadline = editTender?.proposalSubmissionDeadline || '';
      const previousSubmission = editTender?.submissionDeadline || '';
      const prevRemOn = editTender?.assignmentReminderEmail === true;
      const prevRemDays = String(editTender?.assignmentReminderDaysBefore ?? '');
      const prevRemCustom = editTender?.assignmentReminderCustomDate || '';
      const nextRemOn = formSnap?.assignmentReminderEmail === true;
      const nextRemDays =
        formSnap?.assignmentReminderPreset === 'custom' ? '' : String(formSnap?.assignmentReminderPreset ?? '');
      const nextRemCustom =
        formSnap?.assignmentReminderPreset === 'custom' ? (formSnap?.assignmentReminderCustomDate || '').slice(0, 10) : '';
      const isNewTender = !editTender?.id;
      const changed =
        isNewTender ||
        previousAssigned !== assignedUserId ||
        previousDeadline !== proposalDeadline ||
        previousSubmission !== (payload?.submission_deadline || '') ||
        prevRemOn !== nextRemOn ||
        prevRemDays !== nextRemDays ||
        prevRemCustom !== nextRemCustom;
      if (!changed) return;

      const assignedProfile = profiles.find((p) => p.id === assignedUserId);
      const to = pickProfileEmail(assignedProfile);
      if (!to) {
        setToast('Tender saved, but assigned employee email is missing.');
        setTimeout(() => setToast(null), 3500);
        return;
      }

      const tenderTitle = payload?.title || editTender?.title || 'Tender';
      const assignedName = assignedProfile?.full_name || payload?.assigned_employee || 'Team member';
      const portalUrl = `${getPortalUrlForEmails()}/tenders`;
      const ref = tenderCode || payload?.tender_id || editTender?.tenderId || editTender?.referenceNumber || tenderRecordId;
      const submissionDl = payload?.submission_deadline || '';
      let reminderBlock = '';
      if (nextRemOn && submissionDl) {
        const remindDate = computeReminderCalendarDate(submissionDl, formSnap);
        const presetText = describeReminderPreset(formSnap);
        if (remindDate) {
          reminderBlock =
            `\n` +
            `Tender submission deadline: ${submissionDl}\n` +
            `Email reminder: enabled — ${presetText} (planned reminder date: ${remindDate}).\n` +
            `(Automated sending depends on your server configuration; this notice records your schedule.)\n`;
        } else {
          reminderBlock =
            `\n` +
            `Tender submission deadline: ${submissionDl}\n` +
            `Email reminder: enabled — complete the reminder date in the tender if not set.\n`;
        }
      }

      const body =
        `Dear ${assignedName},\n\n` +
        `You have been assigned to work on a tender proposal.\n` +
        `The proposal submission deadline is ${proposalDeadline}.\n\n` +
        `Tender title: ${tenderTitle}\n` +
        `Reference: ${ref || 'N/A'}\n` +
        reminderBlock +
        `\n` +
        `Please log in to the portal and proceed with the required actions:\n` +
        `${portalUrl}\n\n` +
        `Regards,\n` +
        `ECDT Portal`;

      const result = await sendEmail({
        to,
        subject: `Tender Assignment Notification - ${tenderTitle}`,
        body,
      });

      if (!result?.success) {
        setToast(`Tender saved, but assignment email failed: ${result?.error || 'Unknown error'}`);
        setTimeout(() => setToast(null), 4500);
      }
    },
    [editTender, pickProfileEmail, profiles]
  );

  const runWakeUp = useCallback(() => {
    return Promise.race([
      tenderFormSupabase.from('profiles').select('id').limit(1),
      new Promise((_, reject) => setTimeout(() => reject(new Error('wakeup')), 5000)),
    ]).catch(() => {});
  }, []);

  useEffect(() => {
    if (!editTender?.id) {
      setDocuments([]);
      return;
    }
    let cancelled = false;
    setDocumentsLoading(true);
    (async () => {
      const { data, error } = await tenderFormSupabase
        .from('tender_documents')
        .select('*')
        .eq('tender_id', editTender.id)
        .order('created_at', { ascending: true });
      if (cancelled) return;
      setDocumentsLoading(false);
      if (error) {
        setToast('Could not load documents: ' + (error.message || 'Unknown error'));
        setDocuments([]);
        setTimeout(() => setToast(null), 5000);
        return;
      }
      setToast(null);
      const mapped = (data || []).map((row) => ({
        id: row.id,
        storage_path: row.storage_path,
        filename: row.file_name,
        displayName: row.display_name || row.file_name,
        documentType: row.document_type || 'Tender Notice',
        version: row.version || '1',
        uploadDate: row.created_at ? row.created_at.slice(0, 10) : new Date().toISOString().slice(0, 10),
        uploadedBy: row.uploaded_by || '—',
      }));
      setDocuments(mapped);
    })();
    return () => { cancelled = true; };
  }, [editTender?.id]);

  useEffect(() => {
    (async () => {
      const { data } = await tenderFormSupabase.from('departments').select('id, name').order('name');
      setDepartments(data ?? []);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const { data } = await tenderFormSupabase.from('profiles').select('*').order('full_name');
      setProfiles(data ?? []);
    })();
  }, []);

  useEffect(() => {
    if (!departments.length || !form.department || form.departmentId) return;
    const id = departments.find((d) => d.name === form.department)?.id;
    if (id) setForm((prev) => ({ ...prev, departmentId: id }));
  }, [departments, form.department, form.departmentId]);

  useEffect(() => {
    if (!profiles.length || !form.assignedEmployee || form.assignedEmployeeId) return;
    const id = profiles.find((p) => p.full_name === form.assignedEmployee)?.id;
    if (id) setForm((prev) => ({ ...prev, assignedEmployeeId: id }));
  }, [profiles, form.assignedEmployee, form.assignedEmployeeId]);

  const addFiles = (fileList) => {
    if (!fileList?.length) return;
    let hasTooLarge = false;
    const newDocs = [];

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      if (file.size > MAX_FILE_SIZE_BYTES) {
        hasTooLarge = true;
        continue;
      }
      newDocs.push(defaultDoc(file));
    }

    if (newDocs.length) {
      setDocuments((prev) => [...prev, ...newDocs]);
    }
    if (hasTooLarge) {
      setDocError('Some files were larger than 5 MB and were not added.');
    } else {
      setDocError('');
    }
  };

  const handleFileAdd = (e) => {
    const files = e.target?.files;
    addFiles(files);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer?.files;
    addFiles(files);
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleViewOrDownloadDoc = async (storagePath) => {
    const { data } = await tenderFormSupabase.storage.from(TENDER_DOCS_BUCKET).createSignedUrl(storagePath, 60);
    if (data?.signedUrl) window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
  };

  const removeDocument = (id) => {
    setDocuments((prev) => {
      const doc = prev.find((d) => d.id === id);
      if (doc?.storage_path && typeof doc.id === 'string' && doc.id.length === 36) {
        setRemovedDocs((r) => [...r, { id: doc.id, storage_path: doc.storage_path }]);
      }
      return prev.filter((d) => d.id !== id);
    });
  };

  const updateDocument = (id, key, value) => {
    setDocuments((prev) => prev.map((d) => (d.id === id ? { ...d, [key]: value } : d)));
  };

  const handleAiFillFromDocuments = async () => {
    const logPrefix = "[TenderForm][AI]";
    if (!documents.length || aiFilling) return;

    // Only new uploads in this session have raw File objects attached.
    const files = documents.map((d) => d.file).filter(Boolean);
    if (!files.length) {
      setDocError('AI can only analyze documents uploaded in this session. Add at least one file and try again.');
      return;
    }

    console.log(`${logPrefix} starting analysis`, {
      docCount: documents.length,
      fileCount: files.length,
      files: files.map((f) => ({ name: f.name, size: f.size, type: f.type })),
    });

    setAiFilling(true);
    setAiUsed(true);
    setDocError('');
    setToast('Analyzing documents with Gemini…');

    try {
      const parts = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        console.log(`${logPrefix} encoding file`, {
          index: i,
          name: file.name,
          size: file.size,
          type: file.type,
        });
        const b64 = await toBase64(file);
        parts.push({ inline_data: { mime_type: file.type || "application/pdf", data: b64 } });
      }

      parts.push({ text: GEMINI_PROMPT });

      const payload = {
        contents: [{ parts }],
      };

      const res = await fetch(GEMINI_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-goog-api-key": GEMINI_API_KEY,
        },
        body: JSON.stringify(payload),
      });

      console.log(`${logPrefix} received response`, { status: res.status, ok: res.ok });

      const data = await res.json();
      console.debug(`${logPrefix} parsed response JSON`, data);

      if (!res.ok) {
        const msg = data?.error?.message || `HTTP ${res.status}`;
        console.error(`${logPrefix} Gemini returned error`, { status: res.status, body: data });
        setDocError(`AI error: ${msg}`);
        setToast(null);
        return;
      }

      let raw = data?.candidates?.[0]?.content?.parts?.[0]?.text || "";
      console.log(`${logPrefix} raw model output (truncated)`, raw.slice(0, 2000));
      raw = raw.replace(/^```(?:json)?\s*/i, "").replace(/\s*```$/i, "").trim();

      let extracted;
      try {
        extracted = JSON.parse(raw);
      } catch (jsonErr) {
        console.error(`${logPrefix} failed to parse model JSON`, jsonErr, {
          rawPreview: raw.slice(0, 2000),
        });
        setDocError('AI returned an invalid JSON response. Please try again or adjust the documents.');
        setToast(null);
        return;
      }

      console.log(`${logPrefix} extracted fields`, extracted);

      const updates = {};
      const KEYS = [
        "title",
        "referenceNumber",
        "department",
        "projectLocation",
        "issuingAuthority",
        "description",
        "issueDate",
        "clarificationDeadline",
        "submissionDeadline",
        "openingDate",
        "expectedAwardDate",
        "tenderValidityDays",
        "projectDuration",
        "currency",
      ];

      let filledCount = 0;
      KEYS.forEach((key) => {
        const v = extracted[key];
        if (v !== null && v !== undefined && String(v).trim() !== "" && String(v).toLowerCase() !== "null") {
          updates[key] = String(v).trim();
          filledCount += 1;
        }
      });

      console.log(`${logPrefix} applying updates`, updates);

      setForm((prev) => ({
        ...prev,
        ...updates,
      }));

      setToast(`AI filled ${filledCount} field(s) from ${files.length} document(s).`);
      setTimeout(() => setToast(null), 5000);
    } catch (err) {
      console.error(`${logPrefix} failed`, err);
      setDocError(err?.message || 'AI analysis failed. Please try again.');
      setToast(null);
    } finally {
      setAiFilling(false);
    }
  };

  const formToPayload = () => {
    const num = (v) => (v === '' || v == null ? null : Number(v));
    const dateVal = (v) => (v && String(v).trim() ? String(v).trim().slice(0, 10) : null);
    const departmentName = form.departmentId ? departments.find((d) => d.id === form.departmentId)?.name ?? null : null;
    const assignedName = form.assignedEmployeeId ? profiles.find((p) => p.id === form.assignedEmployeeId)?.full_name ?? null : null;
    return {
      tender_id: form.referenceNumber?.trim() || null,
      title: form.title?.trim() || '',
      client: null,
      department: departmentName || form.department?.trim() || null,
      project_location: form.projectLocation?.trim() || null,
      issuing_authority: form.issuingAuthority?.trim() || null,
      description: form.description?.trim() || null,
      notes: form.notes?.trim() || null,
      issue_date: dateVal(form.issueDate),
      clarification_deadline: dateVal(form.clarificationDeadline),
      submission_deadline: dateVal(form.submissionDeadline),
      opening_date: dateVal(form.openingDate),
      expected_award_date: dateVal(form.expectedAwardDate),
      tender_validity_days: form.tenderValidityDays !== '' ? num(form.tenderValidityDays) : null,
      proposal_submission_deadline: dateVal(form.proposalSubmissionDeadline),
      estimated_value: num(form.estimatedValue),
      currency: form.currency?.trim() || 'OMR',
      bid_fee: num(form.bidFee),
      tender_bond_required: Boolean(form.tenderBondRequired),
      bond_amount: num(form.bondAmount),
      project_duration: form.projectDuration?.trim() || null,
      assigned_employee: assignedName || form.assignedEmployee?.trim() || null,
      assigned_to_user_id: form.assignedEmployeeId?.trim() || null,
      internal_review_status: form.internalReviewStatus?.trim() || null,
      approval_required: Boolean(form.approvalRequired),
      approval_notes: form.approvalNotes?.trim() || null,
      status: form.status?.trim() || 'Draft',
      approved: form.approved?.trim() || 'Not approved',
      requested_by: form.requestedBy?.trim() || null,
      submitted_date: dateVal(form.submittedDate),
      approved_by: form.approvedBy?.trim() || null,
      approved_date: dateVal(form.approvedDate),
      assignment_reminder_email_enabled: Boolean(form.assignmentReminderEmail),
      assignment_reminder_days_before:
        !form.assignmentReminderEmail || form.assignmentReminderPreset === 'custom'
          ? null
          : Number(form.assignmentReminderPreset),
      assignment_reminder_custom_date:
        !form.assignmentReminderEmail || form.assignmentReminderPreset !== 'custom'
          ? null
          : dateVal(form.assignmentReminderCustomDate),
    };
  };

  const getTenderFormValidationError = () => {
    if (!form.submissionDeadline?.trim()) {
      return 'Submission deadline is required. Open the Timeline tab and set a date.';
    }
    if (form.assignmentReminderEmail) {
      if (!form.assignedEmployeeId?.trim()) {
        return 'Choose an assigned employee to use email reminders, or turn off “Email reminder”.';
      }
      if (form.assignmentReminderPreset === 'custom') {
        if (!form.assignmentReminderCustomDate?.trim()) {
          return 'Choose a reminder date or select a preset (Assignment tab).';
        }
        const rem = form.assignmentReminderCustomDate.slice(0, 10);
        const sub = form.submissionDeadline.slice(0, 10);
        if (rem > sub) {
          return 'Reminder date cannot be after the submission deadline.';
        }
      }
    }
    return null;
  };

  const getNextTenderId = async () => {
    const year = new Date().getFullYear();
    const prefix = `${year}-DEP-TND-`;
    const { data } = await tenderFormSupabase
      .from('tenders')
      .select('tender_id')
      .ilike('tender_id', `${prefix}%`)
      .order('tender_id', { ascending: false })
      .limit(1);
    const last = data?.[0]?.tender_id;
    const nextNum = last ? (parseInt(last.slice(prefix.length), 10) || 0) + 1 : 1;
    return `${prefix}${String(nextNum).padStart(4, '0')}`;
  };

  const handleSubmitForApproval = async () => {
    const validationErr = getTenderFormValidationError();
    if (validationErr) {
      setToast(validationErr);
      setTimeout(() => setToast(null), 5000);
      setShowSubmitConfirm(false);
      return;
    }
    setSubmitting(true);
    setShowSubmitConfirm(false);
    try {
      await runWakeUp();
      const payload = formToPayload();
      let tenderId;
      if (editTender?.id) {
        tenderId = editTender.id;
        delete payload.tender_id;
        const { error } = await tenderFormSupabase.from('tenders').update(payload).eq('id', tenderId);
        if (error) throw error;
      } else {
        payload.tender_id = payload.tender_id || (await getNextTenderId());
        if (!payload.title?.trim()) payload.title = 'Untitled Tender';
        const { data: inserted, error } = await tenderFormSupabase.from('tenders').insert(payload).select('id').single();
        if (error) throw error;
        tenderId = inserted.id;
      }

      await maybeNotifyAssignedEmployee({
        payload,
        tenderRecordId: tenderId,
        tenderCode: payload.tender_id || null,
        form,
      }).catch((e) => {
        console.warn('[TenderForm] assignment email failed:', e?.message || e);
      });

      const { data: { user } } = await tenderFormSupabase.auth.getUser();
      const uploadedBy = user ? (profiles.find((p) => p.id === user.id)?.full_name || 'Current User') : 'Current User';

      for (const { id: docId, storage_path } of removedDocs) {
        await tenderFormSupabase.storage.from(TENDER_DOCS_BUCKET).remove([storage_path]);
        await tenderFormSupabase.from('tender_documents').delete().eq('id', docId);
      }
      setRemovedDocs([]);

      for (let i = 0; i < documents.length; i++) {
        const doc = documents[i];
        if (doc.file) {
          const safeName = (doc.filename || doc.displayName || 'file').replace(/[^a-zA-Z0-9._-]/g, '_');
          const storagePath = `${tenderId}/${Date.now()}_${i}_${safeName}`;
          const { error: uploadErr } = await tenderFormSupabase.storage.from(TENDER_DOCS_BUCKET).upload(storagePath, doc.file, { upsert: true });
          if (uploadErr) throw uploadErr;
          await tenderFormSupabase.from('tender_documents').insert({
            tender_id: tenderId,
            storage_path: storagePath,
            file_name: doc.filename || safeName,
            display_name: doc.displayName || doc.filename || null,
            document_type: doc.documentType || 'Tender Notice',
            version: doc.version || '1',
            uploaded_by: uploadedBy,
          });
        } else if (doc.storage_path && typeof doc.id === 'string' && doc.id.length === 36) {
          await tenderFormSupabase.from('tender_documents').update({
            display_name: doc.displayName || null,
            document_type: doc.documentType || 'Tender Notice',
            version: doc.version || '1',
          }).eq('id', doc.id);
        }
      }

      onSuccess?.();
      onClose?.();
    } catch (err) {
      setToast(err?.message || 'Failed to save tender.');
      setTimeout(() => setToast(null), 4000);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="tenders-modal-backdrop tenders-form-modal" onClick={onClose}>
      <div className="tenders-modal" onClick={(e) => e.stopPropagation()}>
        <div className="tenders-modal-header">
          <h2 className="tenders-modal-title">{editTender ? 'Edit Tender' : 'Add Tender'}</h2>
          <button type="button" className="tenders-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {toast && <div className="tenders-form-doc-error" role="alert" style={{ marginBottom: '0.5rem' }}>{toast}</div>}
        <div className="tenders-form-tabs">
          {TENDER_FORM_TABS.map((label, index) => (
            <button
              key={label}
              type="button"
              className={`tenders-form-tab ${activeTab === index ? 'active' : ''}`}
              onClick={() => setActiveTab(index)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="tenders-form-body">
          {aiFilling && (
            <div className="tenders-ai-progress" aria-hidden="true">
              <div className="tenders-ai-progress-bar" />
            </div>
          )}
          {activeTab === 0 && (
            <>
              <div
                className="tenders-upload-zone"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => document.getElementById('tender-doc-upload')?.click()}
              >
                Drag and drop files here or click to upload (max 5 MB per file)
              </div>
              {docError && (
                <p className="tenders-form-doc-error" role="alert">
                  {docError}
                </p>
              )}
              <input id="tender-doc-upload" type="file" multiple hidden onChange={handleFileAdd} />
              <ul className="tenders-form-doc-list">
                {documentsLoading ? (
                  <li style={{ padding: '1rem', color: 'var(--text-muted)', fontSize: '0.9rem' }}>Loading documents…</li>
                ) : documents.length === 0 ? (
                  <li style={{ padding: '1rem', color: 'var(--text-muted)', fontSize: '0.9rem' }}>No documents yet.</li>
                ) : (
                  documents.map((doc) => (
                    <li key={doc.id} className="tenders-form-doc-item">
                      <div>
                        <label className="tenders-form-label" style={{ marginBottom: '0.15rem' }}>Document name</label>
                        <input
                          type="text"
                          className="tenders-form-input tenders-form-doc-name-input"
                          placeholder="e.g. Tender notice"
                          value={doc.displayName || doc.filename}
                          onChange={(e) => updateDocument(doc.id, 'displayName', e.target.value)}
                        />
                      </div>
                      <select value={doc.documentType} onChange={(e) => updateDocument(doc.id, 'documentType', e.target.value)}>
                        {TENDER_DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <input
                        type="text"
                        placeholder="Version"
                        value={doc.version}
                        onChange={(e) => updateDocument(doc.id, 'version', e.target.value)}
                        style={{ width: '4rem', padding: '0.25rem' }}
                      />
                      <span className="tenders-form-doc-meta">
                        {doc.uploadDate} · {doc.uploadedBy}
                        {doc.filename && (
                          <>
                            {' · '}
                            <span style={{ fontStyle: 'italic' }}>{doc.filename}</span>
                          </>
                        )}
                      </span>
                      {doc.storage_path && (
                        <button
                          type="button"
                          className="tenders-row-icon-btn"
                          aria-label="View or download"
                          title="View / Download"
                          onClick={() => handleViewOrDownloadDoc(doc.storage_path)}
                        >
                          <IconDownload />
                        </button>
                      )}
                      <button
                        type="button"
                        className="tenders-row-icon-btn"
                        aria-label="Delete"
                        onClick={() => removeDocument(doc.id)}
                      >
                        <IconTrash />
                      </button>
                    </li>
                  ))
                )}
              </ul>
              <div className="tenders-ai-helper">
                <p>
                  Use AI to analyze the uploaded tender documents and pre-fill key fields (reference number, issuing
                  authority, dates, scope summary, etc.). Review and adjust before submitting.
                </p>
                <button
                  type="button"
                  className="tenders-header-btn"
                  onClick={handleAiFillFromDocuments}
                  disabled={!documents.length || aiFilling}
                >
                  {aiFilling ? (
                    <>
                      Analyzing documents
                      <span className="tenders-ai-dots">
                        <span />
                        <span />
                        <span />
                      </span>
                    </>
                  ) : (
                    'Draft fields from documents (AI)'
                  )}
                </button>
                {!documents.length && !aiFilling && (
                  <p className="tenders-ai-helper-note">Upload at least one document to enable the AI.</p>
                )}
              </div>
            </>
          )}

          {activeTab === 1 && (
            <div className="tenders-form-grid">
              <div className={`tenders-form-row ${aiUsed ? 'tenders-ai-field' : ''}`}>
                <label className="tenders-form-label">Tender Title</label>
                <input className="tenders-form-input" placeholder="Tender title" value={form.title} onChange={(e) => update('title', e.target.value)} />
              </div>
              <div className={aiUsed ? 'tenders-ai-field' : ''}>
                <label className="tenders-form-label">Tender Reference Number</label>
                <input className="tenders-form-input" placeholder="e.g. 2026-DEP-TND-0001" value={form.referenceNumber} onChange={(e) => update('referenceNumber', e.target.value)} />
              </div>
              <div className={aiUsed ? 'tenders-ai-field' : ''}>
                <label className="tenders-form-label">Department</label>
                <select className="tenders-form-select" value={form.departmentId || ''} onChange={(e) => update('departmentId', e.target.value)}>
                  <option value="">— Select —</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
              <div className={aiUsed ? 'tenders-ai-field' : ''}>
                <label className="tenders-form-label">Project Location</label>
                <input className="tenders-form-input" placeholder="Location" value={form.projectLocation} onChange={(e) => update('projectLocation', e.target.value)} />
              </div>
              <div className={aiUsed ? 'tenders-ai-field' : ''}>
                <label className="tenders-form-label">Issuing Authority</label>
                <input className="tenders-form-input" placeholder="Authority" value={form.issuingAuthority} onChange={(e) => update('issuingAuthority', e.target.value)} />
              </div>
              <div className={`tenders-form-row ${aiUsed ? 'tenders-ai-field' : ''}`}>
                <label className="tenders-form-label">Description / Notes</label>
                <textarea className="tenders-form-textarea" placeholder="Description" value={form.description} onChange={(e) => update('description', e.target.value)} />
              </div>
              {aiUsed && (
                <p className="tenders-ai-helper-note" style={{ marginTop: '0.5rem' }}>
                  Some fields (reference number, issuing authority, dates, and scope text) may have been drafted by the AI
                  preview based on the uploaded documents. Review and adjust before submitting. (No backend processing yet.)
                </p>
              )}
            </div>
          )}

          {activeTab === 2 && (
            <div className="tenders-form-grid">
              {TIMELINE_FIELDS.map(({ key, label, required }) => (
                <div key={key} className={aiUsed ? 'tenders-ai-field' : ''}>
                  <label className="tenders-form-label">
                    {label}
                    {required ? <span style={{ color: 'var(--danger, #b91c1c)' }}> *</span> : null}
                  </label>
                  <input
                    type="date"
                    className="tenders-form-input"
                    required={Boolean(required)}
                    value={form[key]}
                    onChange={(e) => update(key, e.target.value)}
                  />
                </div>
              ))}
              <div className={aiUsed ? 'tenders-ai-field' : ''}>
                <label className="tenders-form-label">Tender Validity (days)</label>
                <input type="number" className="tenders-form-input" placeholder="90" value={form.tenderValidityDays} onChange={(e) => update('tenderValidityDays', e.target.value)} />
              </div>
            </div>
          )}

          {activeTab === 3 && (
            <div className="tenders-form-grid">
              <div>
                <label className="tenders-form-label">Estimated Tender Value</label>
                <input type="number" className="tenders-form-input" placeholder="0" value={form.estimatedValue} onChange={(e) => update('estimatedValue', e.target.value)} />
              </div>
              <div>
                <label className="tenders-form-label">Currency</label>
                <select className="tenders-form-select" value={form.currency} onChange={(e) => update('currency', e.target.value)}>
                  <option value="OMR">OMR</option>
                  <option value="USD">USD</option>
                </select>
              </div>
              <div>
                <label className="tenders-form-label">Bid Fee</label>
                <input type="number" className="tenders-form-input" placeholder="0" value={form.bidFee} onChange={(e) => update('bidFee', e.target.value)} />
              </div>
              <div className="tenders-form-row tenders-form-toggle-wrap">
                <input type="checkbox" id="tenderBond" checked={form.tenderBondRequired} onChange={(e) => update('tenderBondRequired', e.target.checked)} />
                <label htmlFor="tenderBond">Tender Bond Required</label>
              </div>
              {form.tenderBondRequired && (
                <div>
                  <label className="tenders-form-label">Bond Amount</label>
                  <input type="number" className="tenders-form-input" value={form.bondAmount} onChange={(e) => update('bondAmount', e.target.value)} />
                </div>
              )}
              <div>
                <label className="tenders-form-label">Project Duration</label>
                <input className="tenders-form-input" placeholder="e.g. 12 months" value={form.projectDuration} onChange={(e) => update('projectDuration', e.target.value)} />
              </div>
            </div>
          )}

          {activeTab === 4 && (
            <div className="tenders-form-grid">
              <div>
                <label className="tenders-form-label">Assigned Employee</label>
                <select className="tenders-form-select" value={form.assignedEmployeeId || ''} onChange={(e) => update('assignedEmployeeId', e.target.value)}>
                  <option value="">— Select —</option>
                  {profiles.map((p) => (
                    <option key={p.id} value={p.id}>{p.full_name || p.id}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="tenders-form-label">Deadline to Submit Proposal</label>
                <input
                  type="date"
                  className="tenders-form-input"
                  value={form.proposalSubmissionDeadline}
                  onChange={(e) => update('proposalSubmissionDeadline', e.target.value)}
                />
              </div>
              <div className="tenders-form-row tenders-form-toggle-wrap" style={{ gridColumn: '1 / -1' }}>
                <input
                  type="checkbox"
                  id="assignmentReminderEmail"
                  checked={form.assignmentReminderEmail}
                  onChange={(e) => update('assignmentReminderEmail', e.target.checked)}
                />
                <label htmlFor="assignmentReminderEmail">Send assignment reminder by email</label>
              </div>
              {form.assignmentReminderEmail && (
                <>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label className="tenders-form-label">When to remind (vs tender submission deadline)</label>
                    <p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      Submission deadline is set on the Timeline tab. The reminder schedule is stored with the tender
                      for follow-up (e.g. two days before that date).
                    </p>
                    <select
                      className="tenders-form-select"
                      value={form.assignmentReminderPreset}
                      onChange={(e) => update('assignmentReminderPreset', e.target.value)}
                    >
                      {REMINDER_PRESET_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  {form.assignmentReminderPreset === 'custom' && (
                    <div>
                      <label className="tenders-form-label">Reminder date</label>
                      <input
                        type="date"
                        className="tenders-form-input"
                        value={form.assignmentReminderCustomDate}
                        max={form.submissionDeadline || undefined}
                        onChange={(e) => update('assignmentReminderCustomDate', e.target.value)}
                      />
                    </div>
                  )}
                  {form.submissionDeadline && form.assignmentReminderPreset !== 'custom' && (
                    <div style={{ gridColumn: '1 / -1', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                      Planned reminder date:{' '}
                      <strong>
                        {plannedReminderDate(form.submissionDeadline, form) || '—'}
                      </strong>
                    </div>
                  )}
                  {form.submissionDeadline && form.assignmentReminderPreset === 'custom' && form.assignmentReminderCustomDate && (
                    <div style={{ gridColumn: '1 / -1', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                      Planned reminder date:{' '}
                      <strong>{plannedReminderDate(form.submissionDeadline, form) || '—'}</strong>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === 5 && (
            <div className="tenders-form-grid">
              <div>
                <label className="tenders-form-label">Internal Review Status</label>
                <select className="tenders-form-select" value={form.internalReviewStatus} onChange={(e) => update('internalReviewStatus', e.target.value)}>
                  <option value="Pending">Pending</option>
                  <option value="In Review">In Review</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>
              <div className="tenders-form-row tenders-form-toggle-wrap">
                <input type="checkbox" id="approvalRequired" checked={form.approvalRequired} onChange={(e) => update('approvalRequired', e.target.checked)} />
                <label htmlFor="approvalRequired">Approval Required</label>
              </div>
              <div className="tenders-form-row">
                <label className="tenders-form-label">Approval Notes</label>
                <textarea className="tenders-form-textarea" placeholder="Notes" value={form.approvalNotes} onChange={(e) => update('approvalNotes', e.target.value)} />
              </div>
            </div>
          )}
        </div>

        <div className="tenders-modal-footer">
          <button type="button" className="tenders-footer-btn" onClick={onClose}>Cancel</button>
          {activeTab === 5 ? (
            <button
              type="button"
              className="tenders-footer-btn tenders-footer-btn-primary"
              onClick={() => {
                const err = getTenderFormValidationError();
                if (err) {
                  setToast(err);
                  setTimeout(() => setToast(null), 5000);
                  return;
                }
                setShowSubmitConfirm(true);
              }}
              disabled={submitting}
            >
              Submit for Approval
            </button>
          ) : (
            <button type="button" className="tenders-footer-btn tenders-footer-btn-primary" onClick={() => setActiveTab((i) => Math.min(i + 1, TENDER_FORM_TABS.length - 1))}>
              Next
            </button>
          )}
        </div>
      </div>

      {showSubmitConfirm && (
        <div className="tenders-modal-backdrop" style={{ zIndex: 1001 }} onClick={() => setShowSubmitConfirm(false)}>
          <div className="tenders-modal" style={{ maxWidth: '400px' }} onClick={(e) => e.stopPropagation()}>
            <div className="tenders-modal-header">
              <h2 className="tenders-modal-title">Submit for Approval?</h2>
              <button type="button" className="tenders-modal-close" onClick={() => setShowSubmitConfirm(false)}>×</button>
            </div>
            <div className="tenders-modal-body">
              <p>This tender will be sent for internal approval. Continue?</p>
            </div>
            <div className="tenders-modal-footer">
              <button type="button" className="tenders-footer-btn" onClick={() => setShowSubmitConfirm(false)}>Cancel</button>
              <button type="button" className="tenders-footer-btn tenders-footer-btn-primary" onClick={handleSubmitForApproval} disabled={submitting}>
                {submitting ? 'Submitting…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
